import { CellRef, Rule, RuleType } from "../../components/interfaces/Rule";
import { getRulesFromCustomXml } from "./CustomXmlCoRuleHelper";
import { formatMessage } from "./LanguageHelper";
import { parseRule, tokenizeRule, tokenizeScriptForRendering } from "../grammar/RuleGrammarParser";
import React, { ReactNode, useEffect, useState } from "react";
import { Tag } from "../../components/interfaces/Tag";
import { TruncatedText } from "../../components/generic/truncatedText/TruncatedText";
import { PILL_BASE_STYLE } from "../../components/generic/TrackedChangeOperationTypePill";
import { generateIdentifier } from "./RobotFramework";
import { useAppSelector } from "../../redux/hooks/hooks";
import { selectValidationCoObjects } from "../../redux/feature/validation/validation-slice";
import { getTableCellValues } from "../../office/ContentControl";
import { formatTableTotalsCellAddress, formatTableTotalsCellLabel } from "./TableTotalsHelper";

export const getRules = async (): Promise<Rule[]> => {
  return await getRulesFromCustomXml();
};

const TagPill: React.FC<{ tag: Tag }> = ({ tag }) => {
  const isValid = !tag.isDeleted && Array.isArray(tag.objects) && tag.objects.length > 0;
  const bg = isValid ? "var(--color-blue)" : "var(--color-red)";

  return (
    <span style={{ ...PILL_BASE_STYLE, backgroundColor: bg }}>
      <TruncatedText text={tag.label} />
      {!isValid && (
        <img src="/assets/warningRhombus.svg" alt="warning" style={{ width: 10, height: 10, marginLeft: 5 }} />
      )}
    </span>
  );
};

/**
 * Renderizza lo script sostituendo {{tagId}} con <TagPill />
 * Mantiene il testo normale tra i tag e applica lo stesso stile di RuleInputEditor
 */
export function renderScriptWithTagPills(script: string, tags: Tag[]): ReactNode {
  if (!script) return null;

  const tagMap = new Map(tags.map((t) => [t.id, t]));
  const tokens = tokenizeScriptForRendering(script.replace(/!=/g, "≠"));
  const parts: ReactNode[] = [];

  tokens.forEach((token, i) => {
    if (token.type === "TAG") {
      const tag = tagMap.get(token.tagId);
      if (tag) {
        parts.push(<TagPill key={generateIdentifier("tag", i.toString())} tag={tag} />);
      } else {
        const pseudoTag: Tag = { id: token.tagId, label: token.tagId, objects: [] };
        parts.push(<TagPill key={generateIdentifier("tag-missing", i.toString())} tag={pseudoTag} />);
      }
    } else if (token.type === "OPERATOR") {
      parts.push(
        <span
          key={generateIdentifier("op", i.toString())}
          data-lexical-operator={token.text}
          contentEditable={false}
          data-lexical-decorator="true"
          style={{
            fontWeight: "bolder",
            fontSize: "16px",
            color: "var(--color-blue, #0078d4)",
            display: "inline",
            whiteSpace: "pre",
          }}
        >
          <span style={{ fontWeight: 800, color: "var(--color-blue, #0078d4)" }}>{token.text}</span>
        </span>
      );
    } else if (token.type === "TEXT") {
      // Ogni token di testo viene reso come <span> separato, anche se è solo uno spazio
      for (let j = 0; j < token.text.length; j++) {
        parts.push(
          <span key={`text-${i}-${j}`} data-lexical-text="true" style={{ whiteSpace: "pre" }}>
            {token.text[j]}
          </span>
        );
      }
    }
  });

  // Cambia il contenitore in <div> per abilitare il wrapping automatico
  return <div style={{ whiteSpace: "normal", wordBreak: "break-word" }}>{parts}</div>;
}

/**
 * Renderizza lo script sostituendo {{tagId}} con il valore corrispondente dalla mappa context.
 * Tutto viene renderizzato come nodi testo, senza pill.
 * Se il tagId non è presente in context, viene mostrato il tagId tra parentesi graffe.
 */
export function renderScriptWithValue(script: string, context: Record<string, any>): ReactNode {
  if (!script) return null;

  const tokens = tokenizeScriptForRendering(script.replace(/!=/g, "≠"));
  const parts: ReactNode[] = [];

  for (const [i, token] of tokens.entries()) {
    if (token.type === "TAG") {
      const value = context[token.tagId];
      if (value === undefined) {
        console.error(`[renderScriptWithValue] No value found for object in tag: "${token.tagId}"`);
        return undefined;
      }
      parts.push(
        <span
          key={generateIdentifier("tag-value", i.toString())}
          data-lexical-text="true"
          style={{ whiteSpace: "pre" }}
        >
          {value}
        </span>
      );
    } else if (token.type === "OPERATOR" || token.type === "TEXT") {
      // Tutto come testo
      for (let j = 0; j < token.text.length; j++) {
        parts.push(
          <span key={`text-${i}-${j}`} data-lexical-text="true" style={{ whiteSpace: "pre" }}>
            {token.text[j]}
          </span>
        );
      }
    }
  }

  return <div style={{ whiteSpace: "normal", wordBreak: "break-word" }}>{parts}</div>;
}

/**
 * Validates a rule script using the UserScript grammar parser.
 *
 * @returns `undefined`  — script is valid or is empty/whitespace-only (not an error)
 *          error key    — script failed grammar validation
 *
 * To add a new validation rule, extend RuleGrammarParser.ts.
 */
export const validateRule = (script: string): string => {
  if (!script.trim()) {
    return undefined;
  }
  const isValid = parseRule(script);
  return isValid ? undefined : formatMessage("cof.validation.message.failed.rulevalidationfailed");
};

/**
 * Normalizes a rule script by cleaning up spaces.
 * Ensures there is exactly one space between tags, operators, and values.
 * Delegates tokenization to the ANTLR lexer (RuleGrammarLexer) so that
 * token boundaries are always consistent with the grammar definition.
 */
export const normalizeRule = (script: string): string => {
  if (!script.trim()) {
    return "";
  }
  return tokenizeRule(script).join(" ");
};

export const renderConsistencyContent = (rule: Rule, tags: Tag[]) => {
  const tagIds = rule.tagIds ?? [];
  if (tagIds.length === 0) {
    return (
      <span style={{ color: "var(--color-grey)", fontSize: "13px", fontStyle: "italic" }}>
        {formatMessage("cof.validation.consistency.notags")}
      </span>
    );
  }
  const script = tagIds.map((id) => `{{${id}}}`).join(" ");
  return renderScriptWithTagPills(script, tags);
};

/**
 * Read-only accordion content for TABLE_TOTALS rules.
 * Renders addend and subtotal pills as 1-based (row, column) initially, then loads
 * live values via a deferred async call to getTableCellValues.
 * Null-safe: never throws on malformed rule data.
 */
export const TableTotalsContent: React.FC<{ rule: Rule }> = ({ rule }) => {
  const coObjects = useAppSelector(selectValidationCoObjects);
  const [cellValues, setCellValues] = useState<Array<{ row: number; column: number; value: string }> | null>(null);

  useEffect(() => {
    if (!rule.tableTotals || rule.tableTotals.addends.length === 0) return;

    const { tableId, addends, subtotal } = rule.tableTotals;
    const coObject = coObjects.find((o) => o.id === tableId);
    const contentControlId = coObject?.contentControlId;
    if (!contentControlId) return;

    let mounted = true;
    setCellValues(null);

    getTableCellValues(contentControlId, [...addends, subtotal])
      .then((values) => {
        if (mounted) setCellValues(values);
      })
      .catch(() => {
        // On failure keep coords-only — no spinner, no retry
      });

    return () => {
      mounted = false;
    };
  }, [rule, coObjects]);

  if (!rule.tableTotals || rule.tableTotals.addends.length === 0) {
    return (
      <span style={{ color: "var(--color-grey)", fontSize: "13px", fontStyle: "italic" }}>
        {formatMessage("cof.validation.consistency.notags")}
      </span>
    );
  }

  const { addends, subtotal } = rule.tableTotals;

  const cellText = (cell: CellRef): string => {
    const found = cellValues?.find((v) => v.row === cell.row && v.column === cell.column);
    return formatTableTotalsCellLabel(cell, found?.value);
  };

  return (
    <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "4px" }}>
      {addends.map((addend, i) => (
        <React.Fragment key={`tt-addend-${addend.row}-${addend.column}`}>
          <span style={{ ...PILL_BASE_STYLE, backgroundColor: "var(--color-blue)" }}>
            <TruncatedText text={cellText(addend)} />
          </span>
          {i < addends.length - 1 && <span style={{ fontSize: "12px", fontWeight: 600 }}>+</span>}
        </React.Fragment>
      ))}
      {subtotal && (
        <>
          <span style={{ fontSize: "12px", fontWeight: 600 }}>=</span>
          <span style={{ ...PILL_BASE_STYLE, backgroundColor: "var(--color-blue)" }}>
            <TruncatedText text={cellText(subtotal)} />
          </span>
        </>
      )}
    </div>
  );
};

export function renderTableTotalsContent(rule: Rule): ReactNode {
  return <TableTotalsContent rule={rule} />;
}

export function getRuleBodyAsText(rule: Rule, tags: Tag[]): string {
  if (rule.type === RuleType.CONSISTENCY) return getConsistencyText(rule, tags);
  if (rule.type === RuleType.TABLE_TOTALS) return getTableTotalsText(rule);
  return getRuleScriptText(rule.script, tags);
}

/** Script text with `{{tagId}}` placeholders replaced by their tag labels. */
export function getRuleScriptText(script: string, tags: Tag[]): string {
  if (!script) return "";
  const tagMap = new Map(tags.map((t) => [t.id, t]));
  return tokenizeScriptForRendering(script.replace(/!=/g, "≠"))
    .map((token) => (token.type === "TAG" ? (tagMap.get(token.tagId)?.label ?? token.tagId) : token.text))
    .join("")
    .trim();
}

/** Tag labels of a CONSISTENCY rule joined by a space, or the empty-state message. */
export function getConsistencyText(rule: Rule, tags: Tag[]): string {
  const tagIds = rule.tagIds ?? [];
  if (tagIds.length === 0) {
    return formatMessage("cof.validation.consistency.notags");
  }
  const tagMap = new Map(tags.map((t) => [t.id, t]));
  return tagIds.map((id) => tagMap.get(id)?.label ?? id).join(" ");
}

/** Addend/subtotal coordinates of a TABLE_TOTALS rule, or the empty-state message. */
export function getTableTotalsText(rule: Rule): string {
  if (!rule.tableTotals || rule.tableTotals.addends.length === 0) {
    return formatMessage("cof.validation.consistency.notags");
  }
  const { addends, subtotal } = rule.tableTotals;
  const cellText = formatTableTotalsCellAddress;
  const addendsText = addends.map(cellText).join(" + ");
  return subtotal ? `${addendsText} = ${cellText(subtotal)}` : addendsText;
}
